import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, IconButton, Chip,
  alpha, Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Share as ShareIcon,
  PhotoLibrary as GalleryIcon,
  EmojiEvents as AchievementIcon, Image as ImageIcon, ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon, RestartAlt as ResetIcon, Public as PublicIcon,
  Person as PersonIcon, Schedule as ScheduleIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import FeedbackMessage from '../../common/FeedbackMessage';
import {
  injectDetailsPageStyles,
} from '../../common/DetailsPagePrimitives';
import SystemInformationSection from '../../common/SystemInformationSection';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageGallery } from '../../../utils/galleryPermissions';
import { GALLERY_PUBLICITY_LABELS } from '../../../constants/galleryPublicity';
import StorageImage from '../../common/StorageImage';

// â”€â”€â”€ Design tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';
const CORAL = '#F87171';
const SURFACE_BASE = '#0b1220';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';
const SURFACE_TOP  = '#152640';

/** Top accent bar on GlassPanel (gallery-only). Shared fonts/keyframes come from `injectDetailsPageStyles`. */
const GALLERY_GLASS_CSS = `
  @keyframes bar-sweep { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
`;

const TEXT_BODY = '#E2E8F0';
const TEXT_MUTED = '#64748B';

// â”€â”€â”€ Neon chip â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// â”€â”€â”€ Dark glass panel â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const GlassPanel = ({ children, accentNeon = NEON, sx = {} }) => (
  <Box sx={{
    borderRadius: '14px', overflow: 'hidden', position: 'relative',
    background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.97)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    border: `1px solid ${alpha(accentNeon, 0.17)}`,
    boxShadow: `0 8px 24px ${alpha('#000', 0.35)}`,
    backdropFilter: 'blur(20px)',
    '&::before': {
      content: '""', display: 'block', height: '2px',
      background: `linear-gradient(90deg, transparent, ${alpha(accentNeon, 0.8)} 30%, ${alpha(NEON2, 0.9)} 50%, ${alpha(accentNeon, 0.8)} 70%, transparent)`,
      backgroundSize: '200% 100%', animation: 'bar-sweep 5s linear infinite',
    },
    ...sx,
  }}>
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }} />
    <Box sx={{ position: 'relative', zIndex: 1 }}>{children}</Box>
  </Box>
);

const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to: { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified' }) => {
  const display = value || emptyLabel;
  const isEmpty = !value;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem',
          color: isEmpty ? TEXT_MUTED : TEXT_BODY,
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

// â”€â”€â”€ Publicity / Achievement info â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PUBLICITY_INFO = {
  public:         { icon: '🌐', neon: NEON3,       label: GALLERY_PUBLICITY_LABELS.public },
  private_view:   { icon: '👁', neon: NEON,          label: GALLERY_PUBLICITY_LABELS.private_view },
  private_edit:   { icon: '🔒', neon: '#F87171',   label: GALLERY_PUBLICITY_LABELS.private_edit },
  private_access: { icon: '👥', neon: '#F59E0B',   label: GALLERY_PUBLICITY_LABELS.private_access },
  default:        { icon: '?', neon: alpha(NEON, 0.4), label: 'Unknown' },
};
const ACHIEVEMENT_INFO = {
  international: { icon: 'ðŸŒ', neon: NEON3, label: 'International' },
  domestic:      { icon: 'ðŸ ', neon: NEON,  label: 'Domestic'      },
  default:       { icon: 'ðŸ†', neon: '#F59E0B', label: 'Achievement' },
};
const getPublicityInfo   = (p) => PUBLICITY_INFO[p]  || PUBLICITY_INFO.default;
const getAchievementInfo = (t) => ACHIEVEMENT_INFO[t] || ACHIEVEMENT_INFO.default;

const resolveGalleryAuditName = (record, field) => {
  if (!record) return null;
  if (field === 'created') {
    return record.creator?.name || record.createdBy?.name || null;
  }
  return record.updater?.name || record.updatedBy?.name || record.last_updater?.name || null;
};

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const GalleryDetails = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user: currentUser } = useAuth();

  const [image,              setImage]              = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState(null);
  const [deleteDialogOpen,   setDeleteDialogOpen]   = useState(false);
  const [imageZoom,          setImageZoom]          = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const canManageAllGalleries = canManageGallery(currentUser);
  const logAction = (level, message, payload = {}) => {
    const entry = { module: 'GalleryDetails', galleryId: id, ...payload };
    if (level === 'error') console.error(message, entry);
    else if (level === 'warn') console.warn(message, entry);
    else console.info(message, entry);
  };

  useEffect(() => {
    injectDetailsPageStyles();
    const glassCssId = 'gallerydetails-glass-css';
    if (!document.getElementById(glassCssId)) {
      const el = document.createElement('style');
      el.id = glassCssId;
      el.innerHTML = GALLERY_GLASS_CSS;
      document.head.appendChild(el);
    }
    fetchImage();
  }, [id]);

  const fetchImage = async () => {
    try {
      setLoading(true);
      logAction('info', 'Fetching gallery details');
      const response = await api.get(`/galleries/${id}`);
      setImage(response.data);
      setError(null);
      logAction('info', 'Gallery details loaded');
    } catch (err) {
      logAction('error', 'Failed to load gallery details', { error: err?.message });
      setError('Failed to load image details. Please try again.');
    }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      logAction('warn', 'Deleting gallery item');
      const deletedName = getGalleryDisplayName(image);
      await api.delete(`/galleries/${id}`);
      navigate('/galleries', {
        state: {
          refreshList: Date.now(),
          successMessage: `Gallery "${deletedName}" has been deleted successfully!`,
        },
      });
    } catch (err) {
      logAction('error', 'Failed to delete gallery item', { error: err?.message });
      setError('Failed to delete image. Please try again.');
    }
    setDeleteDialogOpen(false);
  };

  const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();
  const isAchievementImage = (item) => (
    item?.is_achievement === true
    || item?.is_achievement === 1
    || item?.is_achievement === '1'
    || normalizeValue(item?.is_achievement) === 'true'
  );
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: getGalleryDisplayName(image), url: window.location.href });
        logAction('info', 'Shared gallery link via navigator.share');
      } catch {
        navigator.clipboard.writeText(window.location.href);
        logAction('warn', 'Share fallback to clipboard');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      logAction('info', 'Copied gallery link to clipboard');
    }
  };

  const getCurrentImage  = () => {
    if (!image) return null;
    return selectedImageIndex === 0 ? image.representative_picture : image.other_pictures?.[selectedImageIndex - 1] || image.representative_picture;
  };
  const getAllImages       = () => { if (!image) return []; return [image.representative_picture, ...(image.other_pictures || [])]; };
  const getGalleryDisplayName = (gallery) => gallery?.event_name || gallery?.event?.name || gallery?.title || 'Untitled Gallery';
  const parseAchievements = (description) => description ? description.split(/\r?\n/).filter(line => line.trim().length > 0) : [];

  if (loading) return <Loading message="Loading gallery details" size={56} variant="primary" />;

  if (!image) return (
    <Box sx={{ minHeight: '100vh', background: `linear-gradient(135deg, ${SURFACE_BASE}, ${SURFACE_MID})`, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
      <GlassPanel sx={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <Box sx={{ p: 5 }}>
          <GalleryIcon sx={{ fontSize: 72, color: alpha(NEON, 0.2), mb: 2, animation: 'details-float 3s ease-in-out infinite' }} />
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '1.4rem', color: alpha('#E2E8F0', 0.92), mb: 1 }}>Gallery Not Found</Typography>
          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.88rem', color: alpha(NEON, 0.38), mb: 3 }}>The gallery item you're looking for doesn't exist.</Typography>
          <Button variant="contained" onClick={() => navigate('/galleries')}
            sx={{ borderRadius: '10px', px: 4, fontFamily: "'DM Sans', sans-serif", fontWeight: 700, background: alpha(NEON, 0.16), border: `1px solid ${alpha(NEON, 0.38)}`, color: NEON, '&:hover': { color: '#fff', background: alpha(NEON, 0.24), transform: 'translateY(-1px)' } }}>
            Back to Gallery
          </Button>
        </Box>
      </GlassPanel>
    </Box>
  );

  const totalImages     = 1 + (image.other_pictures ? image.other_pictures.length : 0);
  const publicityInfo   = getPublicityInfo(normalizeValue(image.publicity));
  const achievementInfo = getAchievementInfo(image.achievement_type);
  const publicityColor  = publicityInfo.color || publicityInfo.neon || NEON;
  const galleryName = getGalleryDisplayName(image);
  const galleryTypeLabel = isAchievementImage(image) ? 'Achievement Gallery' : 'Regular Gallery';
  const achievementCount = parseAchievements(image.description).length;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>
      <Header
        title="Gallery Details"
        subtitle="View gallery information"
        icon={GalleryIcon}
        showBackButton
        onBackClick={() => navigate('/galleries')}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<ShareIcon sx={{ fontSize: 16 }} />}
              onClick={handleShare}
              sx={{
                borderRadius: '10px', px: 2.5, py: 0.7,
                fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem',
                background: 'transparent', border: `1.5px solid ${alpha(NEON2, 0.6)}`,
                color: alpha(NEON2, 0.95),
                '&:hover': { background: alpha(NEON2, 0.08), borderColor: NEON2, boxShadow: `0 0 12px ${alpha(NEON2, 0.2)}` },
              }}
            >
              Share
            </Button>
            {canManageAllGalleries && (
              <>
                <Button
                  startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                  onClick={() => navigate(`/galleries/edit/${id}`)}
                  sx={{
                    borderRadius: '10px', px: 2.2, py: 0.65,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                    background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
                    color: NEON, transition: 'all 0.2s',
                    '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
                  }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: '10px', px: 2.2, py: 0.65,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                    background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`,
                    color: CORAL, transition: 'all 0.2s',
                    '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` },
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        )}
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <GalleryIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {galleryName}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  {galleryTypeLabel}
                  {image.created_at ? ` · ${new Date(image.created_at).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(publicityColor, 0.1),
              border: `1px solid ${alpha(publicityColor, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <CircleIcon sx={{ fontSize: 7, color: publicityColor }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: publicityColor, letterSpacing: '0.05em' }}>
                {publicityInfo.label}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Total Images" value={String(totalImages)} icon={ImageIcon} color={NEON} />
            <MetricCard label="Publicity" value={publicityInfo.label} icon={PublicIcon} color={publicityColor} />
            <MetricCard label="Gallery Type" value={galleryTypeLabel} icon={GalleryIcon} color={NEON2} />
            <MetricCard label="Achievements" value={isAchievementImage(image) ? String(achievementCount) : 'N/A'} icon={AchievementIcon} color={NEON3} />
          </Box>
        </Box>

        <Section title="Image Viewer" icon={ImageIcon} delay={0} accent={NEON}>
          <Box sx={{ px: 0.5, mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.45), letterSpacing: 1 }}>
              IMAGE {selectedImageIndex + 1} OF {totalImages}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {[
                { icon: ZoomOutIcon, tip: 'Zoom Out', onClick: () => setImageZoom((z) => Math.max(0.5, z - 0.25)), disabled: imageZoom <= 0.5 },
                { icon: ResetIcon, tip: 'Reset', onClick: () => setImageZoom(1), disabled: false },
                { icon: ZoomInIcon, tip: 'Zoom In', onClick: () => setImageZoom((z) => Math.min(3, z + 0.25)), disabled: imageZoom >= 3 },
              ].map(({ icon: Icon, tip, onClick, disabled }, i) => (
                <Tooltip key={i} title={tip}>
                  <Box component="span">
                    <IconButton size="small" onClick={onClick} disabled={disabled}
                      sx={{ borderRadius: '7px', color: disabled ? alpha(NEON, 0.2) : alpha(NEON, 0.65), border: `1px solid ${alpha(NEON, disabled ? 0.08 : 0.18)}`, background: alpha(SURFACE_UP, 0.6), '&:hover': { color: NEON, background: alpha(NEON, 0.08), borderColor: alpha(NEON, 0.38) } }}>
                      <Icon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Tooltip>
              ))}
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', color: alpha(NEON, 0.5), minWidth: 38, textAlign: 'center' }}>
                {Math.round(imageZoom * 100)}%
              </Typography>
            </Box>
          </Box>

          <Box sx={{ position: 'relative', background: '#000', minHeight: 380, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: '10px', border: `1px solid ${alpha(NEON, 0.16)}` }}>
              <StorageImage
                storagePath={getCurrentImage()}
                alt={getGalleryDisplayName(image)}
                style={{ maxWidth: '100%', maxHeight: 520, objectFit: 'contain', transform: `scale(${imageZoom})`, transition: 'transform 0.3s ease', borderRadius: 8 }}
              />
          </Box>

          {image.other_pictures?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, color: alpha(NEON, 0.45), letterSpacing: 1.2, textTransform: 'uppercase', mb: 1.5 }}>
                All Images
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                {getAllImages().map((_, index) => (
                  <Box key={index} onClick={() => setSelectedImageIndex(index)}
                    sx={{ position: 'relative', width: 70, height: 70, borderRadius: '9px', overflow: 'hidden', cursor: 'pointer', flexShrink: 0, border: `2px solid ${selectedImageIndex === index ? NEON : alpha(NEON, 0.15)}`, boxShadow: selectedImageIndex === index ? `0 0 12px ${alpha(NEON, 0.4)}` : 'none', '&:hover': { borderColor: alpha(NEON, 0.5), transform: 'translateY(-2px)' } }}>
                    <StorageImage storagePath={getAllImages()[index]} alt={index === 0 ? 'Main' : `#${index}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Section>

        {isAchievementImage(image) && (
          <Section title="Achievement Details" icon={AchievementIcon} delay={0.1} accent={NEON3}>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <InfoRow icon={AchievementIcon} label="Achievement Type" value={achievementInfo.label} color={NEON3} />
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.62rem', fontWeight: 600, color: alpha(NEON3, 0.65), letterSpacing: 1.2, textTransform: 'uppercase', mb: 1 }}>
                  Achievements
                </Typography>
                {parseAchievements(image.achievement_description).map((achievement, index) => (
                  <Box key={index} sx={{ p: 1.25, borderRadius: '8px', mb: 0.8, background: alpha(NEON3, 0.05), border: `1px solid ${alpha(NEON3, 0.16)}` }}>
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.84rem', color: alpha(TEXT_BODY, 0.78) }}>{achievement}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Section>
        )}
        <SystemInformationSection
          createdBy={resolveGalleryAuditName(image, 'created')}
          createdAt={image.created_at ? new Date(image.created_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null}
          updatedBy={resolveGalleryAuditName(image, 'updated')}
          updatedAt={image.updated_at ? new Date(image.updated_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null}
          updatedAtLabel="Last Updated"
          delay={0.14}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        variant="delete"
        itemType="gallery item"
        itemName={getGalleryDisplayName(image)}
        onConfirm={handleDelete}
      />
    </Container>
  );
};

export default GalleryDetails;
